import React, { useCallback, useEffect, useRef, useState } from 'react';
import MenuItems from '../CreativeStudio/QrCodes/MenuItems';
import MenuPublishing from './MenuPublishing';

function MenuEditor({ site, area, onEditWidget }) {
  const [assets, setAssets] = useState([]);
  const [tab, setTab] = useState('edit');
  const [snapshot, setSnapshot] = useState(null);
  const [assetError, setAssetError] = useState('');
  const active = useRef(false);
  const assetRequest = useRef(null);

  const refreshAssets = useCallback(async () => {
    assetRequest.current?.abort();
    const controller = new AbortController();
    assetRequest.current = controller;
    setAssetError('');
    try {
      const response = await fetch(`/api/creative/assets?siteId=${encodeURIComponent(site.id)}`, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Menu images could not be loaded. Please retry.');
      const data = await response.json();
      if (active.current && !controller.signal.aborted) {
        const nextAssets = Array.isArray(data.assets) ? data.assets : [];
        setAssets(nextAssets);
        return nextAssets;
      }
    } catch (error) {
      if (active.current && !controller.signal.aborted) {
        setAssetError(error.message || 'Menu images could not be loaded. Please retry.');
      }
    }
    return [];
  }, [site.id]);

  useEffect(() => {
    active.current = true;
    refreshAssets();
    return () => {
      active.current = false;
      assetRequest.current?.abort();
    };
  }, [refreshAssets]);

  return (
    <div>
      <nav aria-label="Menu workspace" className="flex flex-wrap gap-2 mb-4">
        {[['edit', 'Edit menu'], ['appearance', 'Appearance'], ['publish', 'Publish']].map(([id, label]) => (
          <button key={id} type="button" aria-pressed={tab === id} onClick={() => setTab(id)}
            className={`rounded px-4 py-2 ${tab === id ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-200'}`}>{label}</button>
        ))}
      </nav>
      <p className="text-sm text-gray-400 mb-4">Dish and price edits update every menu surface. Appearance and Publish settings apply to the published menu and highlights.</p>
      <div hidden={tab !== 'edit'}>
        {assetError && (
          <div role="alert" className="mb-4 text-amber-200">
            {assetError}{' '}
            <button type="button" className="underline" onClick={refreshAssets}>Retry images</button>
          </div>
        )}
        <MenuItems
          site={site}
          area={area}
          assets={assets}
          onRefreshAssets={refreshAssets}
          catalogType="menu"
          onEditWidget={onEditWidget}
          onCatalogLoaded={setSnapshot}
        />
      </div>
      {snapshot?.catalog?.id ? (
        <MenuPublishing key={snapshot.catalog.id} snapshot={snapshot} assets={assets} tab={tab} onEditMenu={() => setTab('edit')} />
      ) : tab !== 'edit' && <p className="text-gray-300">Open Edit menu and save a menu in this area before configuring publishing.</p>}
    </div>
  );
}

export default function MenusWorkspace({ site, area, onEditWidget }) {
  if (!site?.id) return <p className="text-gray-400">Select a venue to manage its menus.</p>;
  const areaSiteId = area?.site_id ?? area?.siteId;
  const belongsToSite = areaSiteId != null
    ? String(areaSiteId) === String(site.id)
    : (site.areas || []).some((entry) => String(entry.id) === String(area?.id));
  if (!area?.id || !belongsToSite) {
    return <p className="text-gray-400">Select an area in this venue to manage its menu.</p>;
  }

  // Remount the existing editor so drafts and late responses stay within their original area.
  return <MenuEditor key={`${site.id}:${area.id}`} site={site} area={area} onEditWidget={onEditWidget} />;
}
